/**
 * lib/invoicePdf.ts
 * ------------------------------------------------------------------
 * Branded A4 invoice PDF via jsPDF. Pulls company identity from
 * CompanySettings and the frozen client + VAT snapshot off the
 * invoice. The VAT column/row only appears when the issued invoice
 * was VAT-registered (dormant otherwise).
 * ------------------------------------------------------------------
 */
import { jsPDF } from 'jspdf';
import type { CompanySettings, Invoice } from '@/types';
import { formatPula } from './money';
import { drawDocGlyph, drawWatermark, brandName } from './pdfIcons';
import { drawBankingDetails } from './pdfHeader';

const ORANGE: [number, number, number] = [255, 107, 53];
const CHAR: [number, number, number] = [34, 39, 46];
const GRAY: [number, number, number] = [120, 128, 138];
const LINE: [number, number, number] = [228, 230, 233];

function fmtDate(iso: string): string {
  return iso
    ? new Date(iso).toLocaleDateString('en-BW', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';
}

export function invoiceFileName(invoice: Invoice): string {
  return `Invoice-${invoice.number ?? 'draft'}.pdf`;
}

export function buildInvoicePdf(invoice: Invoice, company: CompanySettings): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210;
  const M = 15; // margin
  const right = W - M;

  // ---- Header band (inset within print margins, not full-bleed) ----
  const bandY = 12;
  const bandH = 26;
  const bandW = W - 2 * M;
  doc.setFillColor(...ORANGE);
  doc.rect(M, bandY, bandW, bandH, 'F');
  const cy = bandY + bandH / 2;
  // Chevron mark (matches the app wordmark) drawn as vector strokes.
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(1.4);
  doc.setLineCap('round');
  doc.setLineJoin('round');
  const cW = 3.4;
  const chX = M + 8;
  doc.line(chX, cy - 3.4, chX + cW, cy);
  doc.line(chX + cW, cy, chX, cy + 3.4);
  const wordX = chX + cW + 3;
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(brandName(company), wordX, cy - 0.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('PROPRIETARY LIMITED', wordX, cy + 4.5);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('INVOICE', M + bandW - 6, cy + 1.5, { align: 'right' });
  drawDocGlyph(doc, 'invoice', M + bandW / 2, cy, 13, [255, 255, 255]);
  drawWatermark(doc, 'invoice');

  // ---- Company details ----
  let y = 48;
  doc.setTextColor(...GRAY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  const companyLines = [
    company.physicalAddress,
    company.email,
    company.phones?.join('  ·  '),
    company.registrationNumber ? `Reg: ${company.registrationNumber}` : '',
    company.tin ? `TIN: ${company.tin}` : '',
    company.website || '',
  ].filter(Boolean) as string[];
  companyLines.forEach((l) => {
    doc.text(l, M, y);
    y += 5;
  });

  // ---- Invoice meta (right) ----
  const metaTop = 48;
  doc.setTextColor(...CHAR);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  const meta: Array<[string, string]> = [
    ['Invoice no.', invoice.number ?? 'DRAFT'],
    ['Issue date', fmtDate(invoice.issueDate)],
    ['Due date', fmtDate(invoice.dueDate)],
  ];
  let my = metaTop;
  meta.forEach(([k, v]) => {
    doc.setTextColor(...GRAY);
    doc.setFont('helvetica', 'normal');
    doc.text(k, right - 62, my);
    doc.setTextColor(...CHAR);
    doc.setFont('helvetica', 'bold');
    doc.text(v, right, my, { align: 'right' });
    my += 5.5;
  });

  // ---- Bill to ----
  y = Math.max(y, my) + 6;
  doc.setTextColor(...GRAY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('BILL TO', M, y);
  y += 5.5;
  doc.setTextColor(...CHAR);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11.5);
  doc.text(invoice.client.name, M, y);
  y += 5.5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  doc.setTextColor(...GRAY);
  [invoice.client.billingAddress, invoice.client.email, invoice.client.tin ? `TIN: ${invoice.client.tin}` : '']
    .filter(Boolean)
    .forEach((l) => {
      doc.text(l as string, M, y);
      y += 5;
    });

  // ---- Line items table ----
  y += 6;
  const colDesc = M;
  const xQty = 116; // right-aligned
  const xUnit = 152; // right-aligned (unit price)
  const xAmt = right - 2; // right-aligned (amount)
  const descWrap = xQty - colDesc - 8;
  const registered = invoice.vat.registered;

  doc.setFillColor(255, 241, 234);
  doc.rect(M, y - 5, right - M, 9, 'F');
  doc.setTextColor(...CHAR);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text('DESCRIPTION', colDesc + 2, y);
  doc.text('QTY', xQty, y, { align: 'right' });
  doc.text('UNIT PRICE', xUnit, y, { align: 'right' });
  doc.text('AMOUNT', xAmt, y, { align: 'right' });
  y += 7;

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...CHAR);
  invoice.lineItems.forEach((l) => {
    const descLines = doc.splitTextToSize(l.description, descWrap) as string[];
    doc.text(descLines, colDesc + 2, y);
    doc.text(String(l.quantity), xQty, y, { align: 'right' });
    doc.text(formatPula(l.unitPrice), xUnit, y, { align: 'right' });
    doc.text(formatPula(l.lineTotal), xAmt, y, { align: 'right' });
    const rows = Math.max(1, descLines.length);
    y += 5.5 * rows + 1.5;
    doc.setDrawColor(...LINE);
    doc.line(M, y - 3, right, y - 3);
  });

  // ---- Totals ----
  y += 4;
  const labelX = xUnit;
  const totalRow = (label: string, value: string, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 13 : 11);
    doc.setTextColor(...(bold ? CHAR : GRAY));
    doc.text(label, labelX, y, { align: 'right' });
    doc.setTextColor(...CHAR);
    doc.text(value, xAmt, y, { align: 'right' });
    y += bold ? 8 : 6;
  };
  totalRow('Subtotal', formatPula(invoice.totals.subtotal));
  if (registered) totalRow(`VAT (${invoice.vat.rate}%)`, formatPula(invoice.totals.vatTotal));
  doc.setDrawColor(...CHAR);
  doc.line(labelX - 40, y - 1, right, y - 1);
  y += 5;
  totalRow('Total', formatPula(invoice.totals.total), true);

  // ---- Banking + footer ----
  let by = Math.max(y + 8, 232);
  if (company.bank) {
    by = drawBankingDetails(doc, company.bank, M, by);
  }

  const footerText = invoice.notes || company.invoiceFooter;
  if (footerText) {
    doc.setFontSize(10);
    doc.setTextColor(...GRAY);
    const fl = doc.splitTextToSize(footerText, right - M) as string[];
    doc.text(fl, M, 280 - fl.length * 4.6);
  }
  if (!registered) {
    doc.setFontSize(9.5);
    doc.setTextColor(...GRAY);
    doc.text('Not registered for VAT.', right, 290, { align: 'right' });
  }

  return doc;
}

export function downloadInvoicePdf(invoice: Invoice, company: CompanySettings): void {
  buildInvoicePdf(invoice, company).save(invoiceFileName(invoice));
}

/** Base64 (no data: prefix) for emailing as an attachment. */
export function invoicePdfBase64(invoice: Invoice, company: CompanySettings): string {
  const uri = buildInvoicePdf(invoice, company).output('datauristring');
  return uri.slice(uri.indexOf(',') + 1);
}
